import { type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';
import type { SerializedEditorState } from 'lexical';

import type { VideoComment } from 'js/src/client';
import type { CommentUser, SceneComment } from 'js/src/data/comments';
import {
    addingComment,
    clearCommentDraft,
    clearCommentsBySceneId,
    deletedComment,
    deletingComment,
    loadingVideoComments,
    saveCommentDraft,
    storeComments,
} from 'js/src/data/comments/comments-actions';
import StoreORM from 'js/src/data/store-orm';
import type { ImmutableMap } from 'js/src/libs/consts';

/**
 * This stores the raw data from the backend on comments.
 * Comments are sorted by last_updated in comment-operations before stored into the store.
 */
export function comments(
    state = StoreORM.newFromIdPrefix<VideoComment>('comments').data,
    action: AnyAction,
) {
    if (storeComments.match(action)) {
        const orm = new StoreORM<VideoComment>(state);
        action.payload.comments.forEach((comment) => orm.pushJSItem(comment, 'id'));
        return orm.data;
    }
    return state;
}

export function comments_users(
    state = StoreORM.newFromIdPrefix<CommentUser, string>('comments_users').data,
    action: AnyAction,
) {
    if (storeComments.match(action)) {
        const orm = new StoreORM<CommentUser, string>(state);
        // loop through comments and create a map of external users indexed by their email
        action.payload.comments.forEach((comment) => {
            if (comment.external_user_name && comment.external_user_email) {
                const externalUserObj = Immutable.fromJS({
                    external_user_email: comment.external_user_email,
                    external_user_name: comment.external_user_name,
                }) as unknown as ImmutableMap<CommentUser>;

                // if it exists, update instead of pushing
                if (orm.contains(comment.external_user_email)) {
                    orm.updateById(comment.external_user_email, {
                        value: externalUserObj.get('external_user_email'),
                        key: ['external_user_email'],
                    });
                } else {
                    orm.pushItem(externalUserObj, 'external_user_email');
                }
            } else {
                const recognizedUser = comment.user;
                if (!recognizedUser) {
                    throw new Error('Comment does not have a user or external_user_name/email');
                }
                const nonTeamMemberObj = Immutable.fromJS({
                    external_user_email: recognizedUser.email,
                    external_user_name: `${recognizedUser.first_name} ${recognizedUser.last_name}`,
                }) as unknown as ImmutableMap<CommentUser>;

                // if it exists, update instead of pushing
                if (orm.contains(recognizedUser.email)) {
                    orm.updateById(recognizedUser.email, {
                        value: recognizedUser.email,
                        key: ['external_user_email'],
                    });
                } else {
                    orm.pushItem(nonTeamMemberObj, 'external_user_email');
                }
            }
        });
        return orm.data;
    }

    return state;
}

/**
 * This stores the association between scene_ids and comment_ids in the form of { 'scene_id': [comment_ids] }
 */
export function scene_comments(
    state = StoreORM.newFromIdPrefix<SceneComment, string>('scenes').data,
    action: AnyAction,
) {
    if (storeComments.match(action)) {
        const orm = new StoreORM<SceneComment, string>(state);
        action.payload.comments.forEach((comment) => {
            const slideId = comment.slide_id;
            if (!slideId) return;
            const commentId = comment.id;
            const isResolvedComment = !!comment.resolved_at;

            if (orm.contains(slideId)) {
                orm.updateById(slideId, {
                    value: (commentIdList: number[]) => {
                        return commentIdList.push(commentId);
                    },
                    key: ['comment_ids'],
                });

                // keep track of open/resolved threads
                isResolvedComment
                    ? orm.updateById(slideId, {
                          value: (commentIdList: number[]) => {
                              return commentIdList.push(commentId);
                          },
                          key: ['resolved_comment_ids'],
                      })
                    : orm.updateById(slideId, {
                          value: (commentIdList: number[]) => {
                              return commentIdList.push(commentId);
                          },
                          key: ['open_comment_ids'],
                      });
            } else {
                orm.pushJSItem(
                    {
                        slide_id: slideId,
                        comment_ids: [commentId],
                        open_comment_ids: isResolvedComment ? [] : [commentId],
                        resolved_comment_ids: isResolvedComment ? [commentId] : [],
                    },
                    'slide_id',
                );
            }
        });
        return orm.data;
    }
    if (deletedComment.match(action)) {
        const orm = new StoreORM<SceneComment, string>(state);
        const sceneId = action.payload.scene_id || '';
        if (orm.contains(sceneId)) {
            orm.updateById(sceneId, {
                value: (commentIdList: number[]) => {
                    return commentIdList.filter((comment) => comment !== action.payload.comment_id);
                },
                key: ['comment_ids'],
            });

            orm.updateById(sceneId, {
                value: (commentIdList: number[]) => {
                    return commentIdList.filter((comment) => comment !== action.payload.comment_id);
                },
                key: ['open_comment_ids'],
            });

            orm.updateById(sceneId, {
                value: (commentIdList: number[]) => {
                    return commentIdList.filter((comment) => comment !== action.payload.comment_id);
                },
                key: ['resolved_comment_ids'],
            });
        }

        if (orm.getById(sceneId).get('comment_ids').size === 0) {
            orm.delete([sceneId]);
        }

        return orm.data;
    }
    if (clearCommentsBySceneId.match(action)) {
        const orm = new StoreORM<SceneComment, string>(state);
        orm.delete([action.payload.scene_id]);
        return orm.data;
    }
    return state;
}

const initialState = {
    loadingComments: false,
    addingComment: false, // NOTE: as far as I know we're not using this state anywhere
    deletingComment: false, // NOTE: as far as I know we're not using this state anywhere
};
export function comments_ui(state = initialState, action: AnyAction) {
    if (addingComment.match(action)) {
        return {
            ...state,
            addingComment: action.payload.status,
        };
    }
    if (deletingComment.match(action)) {
        return {
            ...state,
            deletingComment: action.payload.status,
        };
    }
    if (loadingVideoComments.match(action)) {
        return {
            ...state,
            loadingComments: action.payload.status,
        };
    }
    return state;
}

export function comment_drafts(
    state: Record<string, SerializedEditorState> = {},
    action: AnyAction,
) {
    if (saveCommentDraft.match(action)) {
        return {
            ...state,
            [action.payload.slide_id]: action.payload.draft,
        };
    }
    if (clearCommentDraft.match(action)) {
        const newState = { ...state };
        delete newState[action.payload.slide_id];
        return newState;
    }
    return state;
}

export const commentsReducer = combineReducers({
    comments,
    scene_comments,
    comments_ui,
    comments_users,
    comment_drafts,
});

export type CommentsState = ReturnType<typeof commentsReducer>;
